/**
 * @fileoverview API модуль для взаимодействия с бэкендом и внешними сервисами.
 * Обеспечивает работу с операциями, геолокацией, загрузкой файлов в S3 хранилище.
 * Поддерживает мультисредовую конфигурацию для разных доменов.
 * 
 * @since 1.0.0
 */

import axios from "axios";
import { logger } from "../../utils/logger";

// TODO: Переделать метод на protected или v1. Токены хардкодить нельзя
/**
 * @deprecated Хардкодированный токен будет заменен на защищенный механизм аутентификации
 * @remarks Временное решение для авторизации API запросов
 */
export const token = "e25d1e31814b65966798346f52713517";

/**
 * Маппинг доменов на конфигурации API для разных сред.
 * 
 * @remarks
 * Поддерживает разные конфигурации для dev, demo, production сред.
 * Каждая среда имеет свои protected ID для безопасности.
 */
type HostnameMap = {
  [key: string]: {
    /** Основной URL API сервера */
    API_URL: string;
    /** ID для защищенных геолокационных запросов */
    GEO_PROTECTED_ID: string;
    /** ID для защищенных операционных запросов */
    OPERATIONS_PROTECTED_ID: string;
    /** URL внешнего API для S3 загрузок */
    EXTERNAL_API_URL: string;
  };
};
/**
 * Конфигурация production-среды.
 *
 * @remarks
 * Вынесена в константу, потому что используется двумя хостами сразу —
 * самим `twa.topdoer.ru` и TLS-прокси перед ним. Так значения не разъедутся.
 */
const PRODUCTION_CONFIG = {
  API_URL: "https://api.ucar.pro",
  GEO_PROTECTED_ID: "6b113646-225f-4181-9a7d-e4cf3b2921a4",
  OPERATIONS_PROTECTED_ID: "4d5e24dd-724f-47e4-a201-754862c2b979",
  EXTERNAL_API_URL: "https://api.moika-moika.site",
};

const hostnameMap: HostnameMap = {
  "dev-twa.topdoer.ru": {
    API_URL: "https://dev-api.ucar.pro",
    GEO_PROTECTED_ID: "a774ab79-b04a-407d-bad7-36dc61b905c5",
    OPERATIONS_PROTECTED_ID: "1589dce4-d5c0-4093-8b18-0e1c9bcd9784",
    EXTERNAL_API_URL: "https://dev-api.moika-moika.site",
  },
  "twa.topdoer.ru": PRODUCTION_CONFIG,
  /**
   * TLS-прокси перед `twa.topdoer.ru`.
   *
   * @remarks
   * Прокси принимает соединение RSA-сертификатом и проксирует на прод, поэтому
   * конфигурация у него production-овская, несмотря на `dev-` в имени.
   */
  "dev-api.topdoer.ru": PRODUCTION_CONFIG,
  "demo-twa.topdoer.ru": {
    API_URL: "https://api.topdoer.ru",
    GEO_PROTECTED_ID: "61e282f7-5386-4ee4-aea7-1612156c3c89",
    OPERATIONS_PROTECTED_ID: "841cdd3e-72b3-4d70-930b-d4eb0bacebb9",
    EXTERNAL_API_URL: "https://admin.topdoer.ru",
  },
  "localhost": {
    API_URL: "https://dev-api.ucar.pro",
    GEO_PROTECTED_ID: "a774ab79-b04a-407d-bad7-36dc61b905c5",
    OPERATIONS_PROTECTED_ID: "1589dce4-d5c0-4093-8b18-0e1c9bcd9784",
    EXTERNAL_API_URL: "https://dev-api.moika-moika.site",
  },
};

if (!hostnameMap[window.location.hostname]) {
  throw new Error("Unknown hostname");
}

const API_URL = hostnameMap[window.location.hostname].API_URL;
let GEO_PROTECTED_ID = hostnameMap[window.location.hostname].GEO_PROTECTED_ID;
let OPERATIONS_PROTECTED_ID =
  hostnameMap[window.location.hostname].OPERATIONS_PROTECTED_ID;
let EXTERNAL_API_URL = hostnameMap[window.location.hostname].EXTERNAL_API_URL;

/**
 * Ответ API для получения адреса по координатам.
 * 
 * @since 1.0.0
 */
interface AddressResponse {
  /** Человекочитаемый адрес по координатам */
  address: string;
  /** Время в формате московского времени */
  time: string;
}

export const apiV1 = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

/**
 * Получает человекочитаемый адрес по координатам через DaData API.
 * 
 * @param latitude - Широта в десятичных градусах
 * @param longitude - Долгота в десятичных градусах
 * 
 * @returns Promise с объектом содержащим адрес и время
 * 
 * @remarks
 * Использует защищенный эндпоинт с GEO_PROTECTED_ID для безопасности.
 * При ошибке возвращает fallback результат с текущим временем Москвы.
 * Все ошибки логируются для мониторинга.
 * 
 * @throws Никогда не выбрасывает ошибки - всегда возвращает результат
 * 
 * @since 1.0.0
 * 
 * @example
 * ```typescript
 * const location = await getAddressTime(55.7558, 37.6176);
 * console.log(location.address); // "Москва, Красная площадь, 1"
 * console.log(location.time); // "26.07.2025, 15:30:45"
 * ```
 */
export const getAddressTime = async (
  latitude: number,
  longitude: number
): Promise<AddressResponse> => {
  const defaultResponse = {
    address: "Address not found",
    time: new Date().toLocaleString("ru-RU", { timeZone: "Europe/Moscow" }),
  };

  try {
    const res = await apiV1.get<AddressResponse>(
      `/protected/${GEO_PROTECTED_ID}/dadata/geolocate/address`,
      {
        params: {
          latitude,
          longitude,
        },
        headers: { "Content-Type": "application/json" },
      }
    );

    if (res.status === 200) {
      return res.data;
    }
    return defaultResponse;
  } catch (error) {
    logger.error("Ошибка получения адреса по координатам:", {
      latitude,
      longitude,
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      geoProtectedId: GEO_PROTECTED_ID
    });
    return defaultResponse;
  }
};

/**
 * Получает данные операции по ее идентификатору.
 * 
 * @param id - Уникальный идентификатор операции
 * 
 * @returns Promise с ответом Axios, содержащим данные операции
 * 
 * @throws {Error} Пробрасывает ошибки сетевых запросов для обработки в компонентах
 * 
 * @remarks
 * Использует защищенный эндпоинт с OPERATIONS_PROTECTED_ID.
 * Логирует ошибки для мониторинга, но пробрасывает их дальше.
 * 
 * @since 1.0.0
 * 
 * @example
 * ```typescript
 * try {
 *   const response = await getOperations("op_123");
 *   const operationData = response.data;
 * } catch (error) {
 *   console.error("Ошибка получения операции:", error);
 * }
 * ```
 */
export const getOperations = async (id: string) => {
  try {
    return apiV1.get(`/protected/${OPERATIONS_PROTECTED_ID}/operations/${id}`);
  } catch (error) {
    logger.error("Ошибка получения данных операции:", {
      operationId: id,
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      operationsProtectedId: OPERATIONS_PROTECTED_ID
    });
    throw error; // Пробрасываем ошибку дальше для обработки в компоненте
  }
};

/**
 * Полезная нагрузка для получения pre-signed URL для загрузки файла.
 * 
 * @since 1.0.0
 */
export type GetFileUrlPayload = {
  /** ID пользователя-исполнителя */
  user_id: string;
  /** Имя файла для сохранения */
  name: string;
  /** Размер файла в байтах */
  size: number;
  /** MIME тип файла (image/jpeg, image/png) */
  mime_type: string;
  /** Тип файла (обычно 'photo') */
  file_type: string;
};

/**
 * Получает pre-signed URL для загрузки файла в S3 хранилище.
 * 
 * @param id - Идентификатор операции
 * @param taskElementId - Идентификатор элемента задачи
 * @param payload - Метаданные файла (имя, размер, MIME тип, ид пользователя)
 * 
 * @returns Promise с ответом Axios, содержащим pre-signed URL
 * 
 * @throws {Error} При ошибках авторизации или сетевых проблемах
 * 
 * @remarks
 * Использует Bearer авторизацию с хардкоденным токеном.
 * Полученный URL имеет ограниченное время жизни для безопасности.
 * 
 * @since 1.0.0
 * 
 * @example
 * ```typescript
 * const payload = {
 *   user_id: "user123",
 *   name: "photo.jpg",
 *   size: 1024000,
 *   mime_type: "image/jpeg",
 *   file_type: "photo"
 * };
 * const response = await getFileUrl("op_123", "task_456", payload);
 * const uploadUrl = response.data.url;
 * ```
 */
export const getFileUrl = async (
  id: string,
  taskElementId: string,
  payload: GetFileUrlPayload
) =>
  apiV1.post(`/v1/operations/${id}/elements/${taskElementId}/files`, payload, {
    headers: { Authorization: "Bearer " + token },
  });

/**
 * Загружает файл в S3 хранилище по pre-signed URL.
 * 
 * @param url - Pre-signed URL для загрузки, полученный от getFileUrl
 * @param body - Объект File для загрузки
 * 
 * @returns Promise с ответом Axios о статусе загрузки
 * 
 * @throws {Error} При ошибках сети, авторизации или проблемах S3
 * 
 * @remarks
 * Использует PUT запрос к внешнему API.
 * Автоматически устанавливает content-type из файла.
 * 
 * @since 1.0.0
 * 
 * @example
 * ```typescript
 * const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
 * await uploadFile("/uploads/user123/photo.jpg", file);
 * ```
 */
export const uploadFile = async (url: string, body: File) =>
  axios.put(EXTERNAL_API_URL + url, body, {
    headers: {
      Authorization: "Bearer " + token,
      "content-type": body.type,
    },
  });

/**
 * Подтверждает успешную загрузку фотографий исполнителя.
 * 
 * @param doerUserId - Идентификатор пользователя-исполнителя
 * @param taskElementId - Идентификатор элемента задачи
 * @param URLs - Массив URL-ов загруженных фотографий
 * 
 * @returns Promise с ответом Axios о статусе подтверждения
 * 
 * @throws {Error} При ошибках сети или некорректных параметрах
 * 
 * @remarks
 * Это финальный шаг в процессе отправки фотографий.
 * После успешного выполнения фотографии считаются отправленными.
 * Преобразует массив URL в требуемый формат для API.
 * 
 * @since 1.0.0
 * 
 * @example
 * ```typescript
 * const urls = [
 *   "/uploads/user123/photo1.jpg",
 *   "/uploads/user123/photo2.jpg"
 * ];
 * await approveDoerPhotos("user123", "task_456", urls);
 * ```
 */
export const approveDoerPhotos = async (
  doerUserId: string,
  taskElementId: string,
  URLs: string[]
) =>
  apiV1.post(`/v1/doer_bot/doers/${doerUserId}/actions/approve_photos`, {
    task_element_id: taskElementId,
    photos: URLs.map((url) => ({ file_path: url })),
  });

/**
 * Централизованный объект API для удобного использования во всем приложении.
 * 
 * Предоставляет единый интерфейс для всех внешних интеграций:
 * - Получение данных операций
 * - Работа с геолокацией и адресами
 * - Загрузка файлов через S3
 * - Подтверждение отправки фотографий
 * 
 * @remarks
 * Этот объект является основным способом взаимодействия с API.
 * Все функции автоматически используют корректную конфигурацию
 * для текущей среды (dev/prod/demo).
 * 
 * @since 1.0.0
 * 
 * @example
 * ```typescript
 * import { api } from './api';
 * 
 * // Получение данных операции
 * const operation = await api.getOperationData('op_123');
 * 
 * // Полный цикл загрузки фото
 * const fileUrlResponse = await api.getFileUrl(opId, taskId, fileData);
 * await api.uploadFile(fileUrlResponse.data.url, file);
 * await api.approveDoerPhotos(userId, taskId, [url]);
 * ```
 */
export const api = {
  /** Получает данные операции по идентификатору */
  getOperationData: (id: string) => getOperations(id),
  /** Получает pre-signed URL для загрузки файла */
  getFileUrl,
  /** Загружает файл в S3 хранилище */
  uploadFile,
  /** Подтверждает успешную загрузку фотографий */
  approveDoerPhotos,
  /** Получает адрес и время по координатам */
  getAddressTime,
};
